'use client';

import { useCallback, useEffect } from 'react';
import config from 'config';
import { useCancelAppointmentMutation } from 'services/userProfileApi';

import { Flex, useTheme, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCancelationAppointment } from 'store/reducers/bookingAppointmentSlice';

import {
  getCancellationModalBodyText,
  getCancellationModalSubtitle,
  getCancellationModalTitle
} from 'utils';

import { CancellationType } from 'types';

import { Button, Modal, Text } from 'ui';

const {
  myAppointments: { contactPhone }
} = config;

const getCancelationType = (
  isGlobalCancelAllowed: boolean,
  isLateCancel: boolean,
  isAllowCancel: boolean
): CancellationType => {
  if (!isGlobalCancelAllowed) {
    return CancellationType.DENIED;
  }

  if (isLateCancel && isAllowCancel) {
    return CancellationType.NOT_ALLOWED;
  }

  if (isAllowCancel) {
    return CancellationType.ALLOWED;
  }

  return CancellationType.DENIED;
};

const CancelationAppointmentModal = () => {
  const {
    colors: {
      palette: {
        components: { simpleErrorModal }
      }
    }
  } = useTheme();

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { cancelationAppointment } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const [cancelAppointment] = useCancelAppointmentMutation();

  const { is_late_cancel, allow_cancel, confirmation_num } =
    cancelationAppointment ?? {};
  const isAllowCancel = Boolean(user?.olb?.allow_cancel);

  const cancellationType = getCancelationType(
    isAllowCancel,
    Boolean(is_late_cancel),
    Boolean(allow_cancel)
  );

  const title = getCancellationModalTitle(cancellationType);
  const subtitle = getCancellationModalSubtitle(cancellationType);
  const bodyText = getCancellationModalBodyText(cancellationType, contactPhone);

  const onClose = useCallback(() => {
    dispatch(setCancelationAppointment(null));
  }, [dispatch]);

  const handleConfirmCancel = async () => {
    onClose();
    await cancelAppointment({ id: confirmation_num ?? 0 });
  };

  useEffect(
    () => () => {
      if (cancelationAppointment !== null) {
        onClose();
      }
    },
    [onClose, cancelationAppointment]
  );

  return (
    <Modal
      isOpen={cancelationAppointment !== null}
      onClose={onClose}
      size={{ base: 'xs', md: '2xl', lg: '2xl', '3xl': '6xl' }}
      isCentered
    >
      <Flex gap={0} flexDirection={{ base: 'column', md: 'row' }}>
        <VStack
          align='center'
          flex='1'
          gap={0}
          px={{ base: '16px', md: '48px', '2xl': '70px' }}
          py={{ base: '30px', md: '30px', lg: '40px', '2xl': '65px' }}
        >
          <Text
            fontSize={{ base: '26px', md: '32px', lg: '38px' }}
            color={simpleErrorModal.titleColor}
            textAlign='center'
            mb={{ base: '12px', md: '20px' }}
          >
            {title}
          </Text>
          <Text
            fontSize={{ base: '18px', md: '20px', lg: '26px' }}
            color={simpleErrorModal.descriptionColor}
            textAlign='center'
            mb={{ base: '12px', md: '16px' }}
          >
            {subtitle}
          </Text>
          <Text
            fontSize={{ base: '14px', md: '16px', lg: '20px' }}
            color={simpleErrorModal.descriptionColor}
            textAlign='center'
            lineHeight={1.3}
          >
            {bodyText}
          </Text>
          <Flex
            w='100%'
            justifyContent='center'
            alignItems='center'
            mt={{ base: '30px', sm: '40px', xl: '50px' }}
            gap={{
              base: '10px',
              md: '20px'
            }}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            {cancellationType !== CancellationType.DENIED && (
              <Button
                w='224px'
                height='45px'
                variant='solid'
                colorScheme='red'
                fontSize='14px'
                fontWeight='bold'
                onClick={handleConfirmCancel}
              >
                Confirm Cancel
              </Button>
            )}
            <Button
              w='224px'
              height='45px'
              variant='outline'
              colorScheme='black'
              fontSize='14px'
              fontWeight='bold'
              onClick={onClose}
            >
              {cancellationType === CancellationType.DENIED
                ? 'Close'
                : 'OK, DO NOT CANCEL'}
            </Button>
          </Flex>
        </VStack>
      </Flex>
    </Modal>
  );
};

export default CancelationAppointmentModal;
